/**
 * Sleep Variability Metrics — measures day-to-day consistency of sleep patterns.
 *
 * Research: Day-to-day variability in sleep efficiency is more predictive of
 * depression than absolute sleep values (Suk et al., 2022). A person averaging
 * 7 hours but swinging between 5 and 9 has a worse mental health profile than
 * someone consistently at 6.5.
 *
 * Primary metric: Coefficient of Variation (CV) = (SD / mean) × 100
 */


// ── Core Statistics ──────────────────────────────────────────────────────────

/**
 * Calculate the coefficient of variation (CV) for a series of values.
 * CV = (standardDeviation / mean) × 100, expressed as a percentage.
 * @returns {{ cv: number, mean: number, sd: number }} or null if fewer than 3 values
 */
export function calcCV(values: number[]): { cv: number; mean: number; sd: number } | null {
  const valid = values.filter(v => v != null && isFinite(v) && !isNaN(v));
  if (valid.length < 3) return null;

  const mean = valid.reduce((s, v) => s + v, 0) / valid.length;
  if (mean === 0) return { cv: 0, mean: 0, sd: 0 };

  const variance = valid.reduce((s, v) => s + (v - mean) ** 2, 0) / valid.length;
  const sd = Math.sqrt(variance);
  const cv = (sd / Math.abs(mean)) * 100;

  return { cv: Math.round(cv * 10) / 10, mean: Math.round(mean * 10) / 10, sd: Math.round(sd * 10) / 10 };
}

// ── Time Conversion Helpers ──────────────────────────────────────────────────

/**
 * Convert a Date to minutes-from-midnight, handling midnight wrap for sleep times.
 * Times after 6 PM (18:00) are treated as "evening" (same day),
 * times before 6 PM are treated as "morning" (next day, add 24h offset).
 * This ensures 23:30 = 1410 min and 00:30 = 1470 min (not 30 min).
 */
function toSleepMinutes(d: Date): number {
  const h = d.getHours();
  const m = d.getMinutes();
  const totalMins = h * 60 + m;
  // If before 6 PM, treat as next-day (add 24 hours for continuity)
  return h < 18 ? totalMins + 1440 : totalMins;
}

// ── Sleep Variability ────────────────────────────────────────────────────────

export interface SleepVariabilityInput {
  efficiency: number;     // 0-100 percentage
  onsetTime: Date;        // bedtime
  wakeTime: Date;         // wake time
  duration: number;       // minutes
}

export interface SleepVariabilityResult {
  efficiencyCV: number;   // CV of sleep efficiency (%)
  onsetCV: number;        // CV of sleep onset time (minutes)
  wakeCV: number;         // CV of wake time (minutes)
  durationCV: number;     // CV of sleep duration (minutes)
  onsetSD: number;        // SD of onset in minutes (for ± display)
  wakeSD: number;         // SD of wake in minutes (for ± display)
  interpretation: SleepVariabilitySeverity;
}

export type SleepVariabilitySeverity = 'low' | 'moderate' | 'high';

/**
 * Calculate sleep variability metrics over a given window.
 * @returns Full variability result, or null if insufficient data (< 3 nights)
 */
export function calcSleepVariability(
  sleepData: SleepVariabilityInput[],
  windowDays: number = 7
): SleepVariabilityResult | null {
  const recent = sleepData.slice(-windowDays);
  if (recent.length < 3) return null;

  const effCV = calcCV(recent.map(d => d.efficiency));
  const durCV = calcCV(recent.map(d => d.duration));
  const onsetCV = calcCV(recent.map(d => toSleepMinutes(d.onsetTime)));
  const wakeCV = calcCV(recent.map(d => toSleepMinutes(d.wakeTime)));

  if (!effCV || !durCV) return null;

  const interp = interpretSleepVariability(effCV.cv);

  return {
    efficiencyCV: effCV.cv,
    onsetCV: onsetCV?.cv ?? 0,
    wakeCV: wakeCV?.cv ?? 0,
    durationCV: durCV.cv,
    onsetSD: onsetCV?.sd ?? 0,
    wakeSD: wakeCV?.sd ?? 0,
    interpretation: interp.severity,
  };
}

/**
 * Interpret sleep efficiency variability into a clinical category.
 * Thresholds based on Suk et al. 2022 and Bei et al. 2016:
 *   efficiencyCV < 5%  → Low (good)
 *   efficiencyCV 5–10% → Moderate (watch)
 *   efficiencyCV > 10% → High (concerning)
 */
export function interpretSleepVariability(efficiencyCV: number): {
  label: string;
  severity: SleepVariabilitySeverity;
  color: string;
} {
  if (efficiencyCV < 5) return { label: 'Low variability', severity: 'low', color: '#22C55E' };
  if (efficiencyCV <= 10) return { label: 'Moderate variability', severity: 'moderate', color: '#F59E0B' };
  return { label: 'High variability', severity: 'high', color: '#EF4444' };
}

/**
 * Normalize sleep efficiency CV to a 0–100 pillar input score.
 * CV < 5% → 100, 5–10% → 60, > 10% → 30
 */
export function normalizeSleepCV(effCV: number): number {
  if (effCV < 5) return 100;
  if (effCV <= 10) return 60;
  return 30;
}
